import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

import { datadogRum } from '@datadog/browser-rum';
import { getTranslate } from 'react-localize-redux';
import { connect } from 'react-redux';
import { type RouteComponentProps, Switch, withRouter } from 'react-router';
import { compose, setDisplayName } from 'recompose';

import { accountActions } from '../actions/account.actions';
import { appsActions } from '../actions/apps.actions';
import { useFlipdishAssistantEnabled } from '../components/FlipdishAgent/FlipdishAgentAssistant';
import Loading from '../components/Loading';
import { getOrgById } from '../components/RMS/organisation.services';
import { isAcceptInviteFlowInProgress } from '../components/Teammates/teammateutils';
import { useIsWebView } from '../custom-hooks/useIsWebView';
import { usePageTracking } from '../custom-hooks/usePageTracking';
import { isFlipdishStaffEmail } from '../helpers/account';
import { gdpr } from '../helpers/gdpr';
import { store } from '../helpers/store';
import { isMobileDevice } from '../helpers/utilities';
import ErrorScreen from '../layouts/Portal/ErrorScreen';
import { getAccount } from '../selectors/account.selector';
import { flagService } from '../services';
import { useTracking } from '../services/amplitude/useTracking';
import { getAuthorizedOrgs } from '../services/authorizedOrgs.service';
import { sendDatadogRumData } from '../services/datadogRUM';
import {
  initializeFeatureFlags,
  setFeatureFlagContext,
} from '../services/featureFlags/featureFlagBootstrap';
import { useIntercom, useProductionIntercomAppId } from '../services/intercom/useIntercom';
import { initialise as initaliseDataDogLogging, logger } from '../services/loggerService';
import { accountRoutesConst } from './account.routes';
import { externalSupportUrl } from './accountRoutePaths';
import {
  createRoute,
  DEFAULT_MIN_LOADING_TIMEOUT,
  getAppId,
  isAppLessPrivateRoute,
  isOrgInPathName,
  isPublicRoute,
} from './utils';

// App-less routes do not carry an app id in the URL, so we use the existing
// flipdish-global lookup token to resolve an accessible app when needed.
const DEFAULT_APP_LOOKUP_ID = 'flipdish-global';
const getResolvedAppHomePath = (appId: string) => `/${appId}/home`;
const fetchHasAuthorizedOrgs = async (accountId: number) => {
  const response = await getAuthorizedOrgs({
    type: 'User',
    id: String(accountId),
  });
  return (response.allowedResources?.length ?? 0) > 0;
};
const isSupportPath = (pathname: string) => pathname === accountRoutesConst.Support;
const isSupportIntent = ({
  pathname,
  search,
}: Pick<RouteComponentProps['location'], 'pathname' | 'search'>) => {
  const searchParams = new URLSearchParams(search);

  return isSupportPath(pathname) || searchParams.get('open') === 'support';
};

type Props = RouteComponentProps & MappedState & MappedDispatch;
const AuthRouter: React.FC<React.PropsWithChildren<Props>> = (props) => {
  const isAcceptInviteInProgress = isAcceptInviteFlowInProgress();

  const {
    account,
    currentApp,
    registeredRoutes,
    location,
    history,
    permissions,
    setCurrentApp,
    intercomCompanySyncEnabled,
    translate,
  } = props;

  const isWebView = useIsWebView();
  const { identifyUser } = useTracking(account);
  const [loading, setLoading] = useState(true);
  const [initialized, setInitialized] = useState(false);
  const [appBootstrapError, setAppBootstrapError] = useState(false);
  const [intercomInitialized, setIntercomInitialized] = useState(false);
  const { showNewMessage: showNewIntercomMessage, update: updateIntercom } = useIntercom();
  const isAppLessRoute = isAppLessPrivateRoute(location.pathname);
  const isNoOrganisationAccessRoute = location.pathname === accountRoutesConst.NoOrganisationAccess;
  const selectedAppPermissionsLoaded = permissions.length > 0;
  const flipdishAssistantEnabled = useFlipdishAssistantEnabled();

  //#region initialize
  const startLoadingTime = useRef<number>();
  const preLoginLoadingTimeout = useRef<ReturnType<typeof setTimeout>>();
  const latestAuthorized = useRef(!!account.authorized);
  const previousAuthorized = useRef(!!account.authorized);
  const pathBefore = useRef<string>();
  const pendingSupportMessage = useRef<string | null>(null);
  const authorizedOrgsCheckRef = useRef<{
    accountId: number;
    hasOrgs: boolean;
  } | null>(null);

  useLayoutEffect(() => {
    const isAuthorized = !!account.authorized;
    const wasAuthorized = previousAuthorized.current;
    latestAuthorized.current = isAuthorized;
    previousAuthorized.current = isAuthorized;

    if (isAuthorized && preLoginLoadingTimeout.current) {
      clearTimeout(preLoginLoadingTimeout.current);
      preLoginLoadingTimeout.current = undefined;
    }

    if (isAuthorized && !wasAuthorized) {
      // Auth0 callback authorized a session after the public splash may already
      // have been released. Re-gate until AuthZ / app selection finish.
      setAppBootstrapError(false);
      setInitialized(false);
      setLoading(true);
    }

    if (!isAuthorized && wasAuthorized) {
      authorizedOrgsCheckRef.current = null;
    }
  }, [account.authorized]);

  useEffect(() => {
    startLoadingTime.current = Date.now();

    (function setPathBefore() {
      const { pathname } = location;
      // now any route matches private /:appId/ :(
      if (
        pathname !== '/' &&
        !isAppLessPrivateRoute(pathname) &&
        !isOrgInPathName(pathname) &&
        !isPublicRoute(pathname)
      ) {
        pathBefore.current = pathname;
      }
    })();

    props.getAccountDetails().catch(() => {
      if (latestAuthorized.current) {
        return;
      }

      // No existing session. Keep the splash visible for its minimum duration,
      // unless the Auth0 callback authorizes the account in the meantime.
      preLoginLoadingTimeout.current = setTimeout(
        () => {
          preLoginLoadingTimeout.current = undefined;
          if (!latestAuthorized.current) {
            setInitialized(true);
          }
        },
        Math.max(0, DEFAULT_MIN_LOADING_TIMEOUT - (Date.now() - startLoadingTime.current!))
      );
    });

    return () => {
      if (preLoginLoadingTimeout.current) {
        clearTimeout(preLoginLoadingTimeout.current);
      }
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const accountId = account.AccountId;
    const restoreRequestedPath = () => {
      if (!pathBefore.current) {
        return;
      }

      const path = pathBefore.current;
      pathBefore.current = undefined;
      if (path !== location.pathname) {
        history.replace(path);
      }
    };

    if (account.authorized && accountId) {
      (async () => {
        // AuthZ is the source of truth for authorized orgs. Removed teammates can still
        // resolve legacy apps, so gate on getAuthorizedOrgs before app selection.
        // Skip for Flipdish staff — AuthZ getAuthorizedOrgs does not cover Admin/Factory.
        if (!isFlipdishStaffEmail(account.Email)) {
          let hasAuthorizedOrgs = true;
          const authorizedOrgsCheck = authorizedOrgsCheckRef.current;
          if (authorizedOrgsCheck?.accountId === accountId) {
            hasAuthorizedOrgs = authorizedOrgsCheck.hasOrgs;
          } else {
            try {
              hasAuthorizedOrgs = await fetchHasAuthorizedOrgs(accountId);
            } catch (error) {
              if (cancelled) {
                return;
              }
              // AuthZ outage is not "no organisation access".
              logger.error('Auth router: AuthZ getAuthorizedOrgs failed during bootstrap', {
                accountId,
                email: account.Email,
                pathname: location.pathname,
                error,
              });
              setAppBootstrapError(true);
              setInitialized(true);
              setLoading(false);
              return;
            }
            if (cancelled) {
              return;
            }
            authorizedOrgsCheckRef.current = {
              accountId,
              hasOrgs: hasAuthorizedOrgs,
            };
          }

          if (!hasAuthorizedOrgs) {
            if (!isAppLessRoute) {
              history.replace(accountRoutesConst.NoOrganisationAccess);
            }
            pathBefore.current = undefined;
            setInitialized(true);
            setLoading(false);
            return;
          }
        }

        if (isNoOrganisationAccessRoute && currentApp.AppId) {
          history.replace(getResolvedAppHomePath(currentApp.AppId));
          setInitialized(true);
          setLoading(false);
          return;
        }

        if (currentApp.AppId) {
          pathBefore.current = undefined;
          if (selectedAppPermissionsLoaded || isAppLessRoute) {
            setInitialized(true);
          }
          return;
        }

        if (!currentApp.AppId) {
          if (isOrgInPathName(location.pathname)) {
            setInitialized(true);
            setLoading(false);
            return;
          }

          // This will return FD-global app for FD staff always
          // non fd but existing client will get one or more apps back based on their account cookie
          // new signup will get no apps back

          let app: Awaited<ReturnType<typeof setCurrentApp>>;
          const appLookupId = isNoOrganisationAccessRoute
            ? DEFAULT_APP_LOOKUP_ID
            : getAppId(pathBefore);
          try {
            app = await setCurrentApp(appLookupId, {
              // Suppress the toast only on the no-org route itself. Invalid brand URLs
              // keep the legacy No_app_found toast + portal shell behavior.
              suppressNoAppFoundError: isNoOrganisationAccessRoute,
              // Do not navigate to '/' on network failure — that races AuthRouter and
              // can cause remount loops. Rejected lookups use ErrorScreen instead.
              throwOnError: true,
            });
          } catch (error) {
            if (cancelled) {
              return;
            }
            logger.error('Auth router: app lookup failed during bootstrap', {
              accountId,
              email: account.Email,
              pathname: location.pathname,
              appLookupId,
              error,
            });
            // Network / cancelled lookup on no-org: leave the false no-org URL.
            // Root re-runs bootstrap; if it still rejects, ErrorScreen shows there.
            if (isNoOrganisationAccessRoute) {
              history.replace('/');
              pathBefore.current = undefined;
              return;
            }
            // Network / cancelled lookup: stay put and show a full-page error.
            // Successful empty Data: [] stays below and is not treated as no-org.
            setAppBootstrapError(true);
            setInitialized(true);
            setLoading(false);
            return;
          }
          if (cancelled) {
            return;
          }

          if (isNoOrganisationAccessRoute && app?.AppId) {
            history.replace(getResolvedAppHomePath(app.AppId));
            setInitialized(true);
            setLoading(false);
            return;
          }

          if (!app?.AppId) {
            // AuthZ already confirmed org access (or staff skipped AuthZ). Empty app
            // lookup must not leave users on a false /no-organisation-access page.
            if (isNoOrganisationAccessRoute) {
              history.replace('/');
              pathBefore.current = undefined;
              return;
            }

            if (isAppLessPrivateRoute(location.pathname)) {
              pathBefore.current = undefined;
              setInitialized(true);
              setLoading(false);
              return;
            }

            // Invalid / unmatched brand URL: stay put. setCurrentApp already showed
            // the No_app_found toast (legacy broken-shell behavior).
            setInitialized(true);
            setLoading(false);
            return;
          }

          restoreRequestedPath();
        }
      })();
    }

    return () => {
      cancelled = true;
    };
  }, [
    account,
    currentApp,
    history,
    isAppLessRoute,
    location.pathname,
    setCurrentApp,
    selectedAppPermissionsLoaded,
    isNoOrganisationAccessRoute,
  ]);
  //#endregion

  //#region page events trackers
  usePageTracking();
  //#endregion

  //#region GDPR -init on first load if not on create_password page
  gdpr.useInitGdpr();
  //#endregion

  //#region Feature flags - init providers on first load independently of GDPR-controlled RUM
  useEffect(() => {
    initializeFeatureFlags({
      enableExposureLogging: true,
      enableRumFeatureFlagTracking: true,
    });
  }, []);
  //#endregion

  //#region Split IO -init on first load to retrieve feature flags from Split
  const [flipdishStaffState, setFlipdishStaffState] = useState({
    accountId: account.AccountId,
    isFlipdishStaff: permissions?.includes('FlipdishStaff') ?? false,
  });
  const hasSelectedAppPermissions = Boolean(permissions?.length);
  const hasFlipdishStaffPermission = permissions?.includes('FlipdishStaff') ?? false;
  const isSameAccount = flipdishStaffState.accountId === account.AccountId;

  // Brand switches clear tenant permissions while the replacement set loads.
  // Keep the established account identity stable across only that empty interval.
  const isFlipdishStaff =
    isSameAccount && !hasSelectedAppPermissions
      ? flipdishStaffState.isFlipdishStaff
      : hasFlipdishStaffPermission;

  useEffect(() => {
    if (!isSameAccount || hasSelectedAppPermissions) {
      setFlipdishStaffState((current) => {
        if (
          current.accountId === account.AccountId &&
          current.isFlipdishStaff === hasFlipdishStaffPermission
        ) {
          return current;
        }

        return {
          accountId: account.AccountId,
          isFlipdishStaff: hasFlipdishStaffPermission,
        };
      });
    }
  }, [account.AccountId, hasFlipdishStaffPermission, hasSelectedAppPermissions, isSameAccount]);

  useEffect(() => {
    const isLoggedIn = !!account?.Email;
    const TRAFFIC_TYPE = flagService.TRAFFIC_TYPE;
    const splitParams = {
      appId: currentApp.AppId,
      orgId: currentApp.OrgId,
      isFlipdishStaff,
      userId: account.AccountId || account.DeviceId,
      languageId: account.Language,
      trafficType: isLoggedIn
        ? TRAFFIC_TYPE.USER_TRAFFIC_TYPE
        : TRAFFIC_TYPE.ANONYMOUS_USER_TRAFFIC_TYPE,
    };

    flagService.initialiseSplit(store, splitParams);
    sendDatadogRumData(account, currentApp);
    setFeatureFlagContext(account, currentApp, isFlipdishStaff);
    identifyUser();
  }, [account?.AccountId, currentApp?.AppId, currentApp?.OrgId, isFlipdishStaff]);
  //#endregion

  //#region DataDog -init on first load to allow logging of errors
  useEffect(() => {
    initaliseDataDogLogging();
  }, []);
  //#endregion

  useEffect(() => {
    if (loading && isAcceptInviteInProgress && account.authorized) {
      setLoading(false);
    }
  }, [loading, isAcceptInviteInProgress, account.authorized]);

  //#region Intercom
  const allowIntercomForFlipdishStaff = useProductionIntercomAppId ? !isFlipdishStaff : true;
  // Initialize Intercom after permissions are loaded, but only for non-staff users in production
  useEffect(() => {
    const permissionsLoaded = permissions && permissions.length > 0;
    if (
      permissionsLoaded &&
      allowIntercomForFlipdishStaff &&
      intercomCompanySyncEnabled &&
      !intercomInitialized &&
      !isWebView &&
      !isAppLessRoute
    ) {
      (async () => {
        await gdpr.initGdprIntercomAfterLoad();
        setIntercomInitialized(true);
      })();
    }
  }, [
    permissions,
    allowIntercomForFlipdishStaff,
    intercomCompanySyncEnabled,
    intercomInitialized,
    isWebView,
    isAppLessRoute,
  ]);

  useEffect(() => {
    if (intercomInitialized) {
      // When the Flipdish Assistant replaces chat support, hide Intercom's FAB.
      updateIntercom({
        hide_default_launcher: isWebView || isAppLessRoute || flipdishAssistantEnabled,
      });
    }
  }, [isWebView, isAppLessRoute, intercomInitialized, flipdishAssistantEnabled, updateIntercom]);

  useEffect(() => {
    if (isSupportIntent(location)) {
      pendingSupportMessage.current = new URLSearchParams(location.search).get('message') ?? '';
    }
  }, [location.pathname, location.search]);

  useEffect(() => {
    if (pendingSupportMessage.current === null) {
      return;
    }

    if (isMobileDevice() && !isFlipdishStaff) {
      pendingSupportMessage.current = null;
      window.location.replace(externalSupportUrl);
      return;
    }

    if (isSupportPath(location.pathname) && !currentApp.AppId) {
      return;
    }

    const supportMessage = pendingSupportMessage.current;
    const searchParams = new URLSearchParams(location.search);
    searchParams.delete('open');
    searchParams.delete('message');

    const nextLocation = {
      ...location,
      pathname: isSupportPath(location.pathname)
        ? getResolvedAppHomePath(currentApp.AppId!)
        : location.pathname,
      search: searchParams.toString() ? `?${searchParams.toString()}` : '',
    };

    if (isFlipdishStaff) {
      pendingSupportMessage.current = null;
      history.replace(nextLocation);
      return;
    }

    if (!intercomInitialized) {
      return;
    }

    showNewIntercomMessage(supportMessage);
    pendingSupportMessage.current = null;
    history.replace(nextLocation);
  }, [
    currentApp.AppId,
    history,
    intercomInitialized,
    isFlipdishStaff,
    location.pathname,
    location.search,
    showNewIntercomMessage,
  ]);

  const routes = useMemo(() => {
    if (loading && !isAcceptInviteInProgress) {
      return null;
    }

    if (!account.authorized) {
      datadogRum.setUserProperty('setup_status', 'not_authenticated');
      datadogRum.setGlobalContextProperty('setup_status', 'not_authenticated');
      return registeredRoutes
        .filter((r) => r.group === 'public' || r.group === 'publicAndPrivate')
        .map((r) => createRoute(r, permissions));
    }

    if (isOrgInPathName(location.pathname)) {
      return null;
    }

    datadogRum.setUserProperty('setup_status', 'logged_in');
    datadogRum.setGlobalContextProperty('setup_status', 'logged_in');

    return registeredRoutes
      .filter((r) => r.group === 'private' || r.group === 'publicAndPrivate')
      .map((r) => createRoute(r, permissions));
  }, [loading, account, registeredRoutes, permissions, location.pathname]);

  if (loading) {
    return (
      <Loading
        fullscreen
        animate={!initialized}
        onAnimationEnd={() => {
          // When setInitialized(true) onAnimationEnd sets loading false
          setLoading(false);
        }}
      />
    );
  }

  if (appBootstrapError) {
    return <ErrorScreen translate={translate} />;
  }

  return <Switch>{routes}</Switch>;
};

type MappedState = ReturnType<typeof mapStateToProps>;
const mapStateToProps = (state: AppState) => {
  return {
    account: getAccount(state),
    currentApp: state.currentApp,
    permissions: state.permissions,
    registeredRoutes: state.routing.routes,
    intercomCompanySyncEnabled: flagService.isFlagOn(state, 'enableIntercomCompanySync'),
    translate: getTranslate(state.locale),
  };
};

type MappedDispatch = ReturnType<typeof mapDispatchToProps>;
const mapDispatchToProps = (dispatch: ThunkDispatch) => ({
  getAccountDetails: () => dispatch(accountActions.getAccountDetails(true)),
  setCurrentApp: (
    appId: string | undefined,
    options?: Parameters<typeof appsActions.setCurrentApp>[1]
  ) => dispatch(appsActions.setCurrentApp(appId, options)),
});

export default compose<Props, Record<string, never>>(
  setDisplayName('AuthRouter'),
  withRouter,
  connect(mapStateToProps, mapDispatchToProps)
)(AuthRouter);
